import React, {useContext, useMemo} from 'react';
import {IncompleteAdmissionCRF} from '@/api/dto/dto';
import SortableTable, {SORT_ORDER, TableColumn, TableData} from '@common/SortableTable';
import {SortOrderType} from '@clients/ClientsPage';
import {ColoredTableLink} from '@common/TableLink.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import {SearchParam} from "@/helpers/search_params";
import AssignedToClient from "@clients/AssignedToClient";
import styled from '@emotion/styled';
import {FormCodeBinding} from "@/api/dto/forms";
import {useQuery} from "@tanstack/react-query";
import {CarecoApiContext} from "@/app-context/careco-api-context";

const TableWrapper = styled.div`
    margin-top: 2rem;
`;

interface Props {
    clients: IncompleteAdmissionCRF[];
    loading: boolean;
    fetching: boolean;
    loadError: Error | null;
    searchTerm: string;
    searchFunction: (searchTerm: string) => void;
    sortFunction: (sortKey: TableColumn, sortOrder: SortOrderType) => void;
    onNextPage: () => void;
}

const tableHeaders = [
  'Client name',
  'Client Id',
  'Group',
  'Client admission date',
  'No of Days since admission',
  'Assigned to',
  'CRF form',
  'CRF completed status'
];

const IncompleteCRFsTable = ({
  clients,
  loading,
  fetching,
  loadError,
  searchTerm,
  searchFunction,
  sortFunction,
  onNextPage,
}: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const { data: rosters, isLoading } = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi
  });
  const tableData: TableData[] = useMemo(() => {
    return clients.map((client): TableData => {
      const clientRoster = rosters?.data.data.find(roster => roster.id === client.rosterId);
      const [firstName, middleName, lastName] = client.clientName.split(' ');
      const clientName = DisplayHelpers.formatName(firstName, middleName, lastName);
      const daysSinceAdmission = client.clientAdmissionDate
        ? Math.floor(
          (
            new Date().setUTCHours(0, 0, 0, 0) -
                        new Date(client.clientAdmissionDate).setUTCHours(0, 0, 0, 0)
          ) / (1000 * 60 * 60 * 24)
        )
        : 0;
      return {
        clientName: {
          tableHeader: 'Client name',
          key: 'clientName',
          type: 'string',
          value: clientName,
          displayValue: (
            <ColoredTableLink
              to={`/clients/profile?${SearchParam.PATIENT_ID}=${client.patientId}&${SearchParam.ROSTER_ID}=${client.rosterId}`}>
              {clientName}
            </ColoredTableLink>
          ),
          columnWidth: '18%',
        },
        clientId: {
          tableHeader: 'Client Id',
          key: 'clientId',
          type: 'string',
          value: client.clientId,
          displayValue: client.clientId,
          columnWidth: '14%',
        },
        groupName: {
          tableHeader: 'Group',
          key: 'groupName',
          type: 'string',
          value: client.clientGroupAdmission,
          displayValue: client.clientGroupAdmission,
          columnWidth: '14%',
        },
        admissionDate: {
          tableHeader: 'Client admission date',
          key: 'clientAdmissionDate',
          type: 'date',
          value: client.clientAdmissionDate,
          displayValue: DisplayHelpers.formatDateFromDateOnlyString(client.clientAdmissionDate),
          columnWidth: '14%',
        },
        daysSinceAdmission: {
          tableHeader: 'No of Days since admission',
          key: 'clientAdmissionDate',
          type: 'date',
          value: client.clientAdmissionDate,
          displayValue: daysSinceAdmission,
          columnWidth: '13%',
        },
        assignedUser: {
          tableHeader: 'Assigned to',
          key: 'assignedUser',
          type: 'string',
          value: '',
          displayValue: <AssignedToClient client={client?.client} roster={clientRoster} />,
          columnWidth: '10%',
        },
        incompleteForm: {
          tableHeader: 'CRF form',
          key: 'incompleteForm',
          type: 'string',
          value: '',
          displayValue: client.contractedProvider ? (
            <ColoredTableLink
              to = {`/clients/profile/forms`+
                `?${SearchParam.PATIENT_ID}=${client?.patientId}` +
                `&${SearchParam.ROSTER_ID}=${client?.rosterId}` +
                `&${SearchParam.FORM_TYPE}=${FormCodeBinding.DSAMH_CRF_ADMISSION_FORM}` +
                `&${SearchParam.FORM_ENCOUNTER}=${client?.encounterId}` +
                `&${SearchParam.FORM_EVENT_TYPE}=${rosters?.data.data.find(roster => 
                  roster.id === client?.rosterId)?.typeDisplayName ?? ''}` +
                `&${SearchParam.FORM_EVENT_DATE}=${client?.clientAdmissionDate ?? ''}`}>

                Admission CRF
            </ColoredTableLink>
          ) : (
            'N/A'
          ),
          columnWidth: '9%',
        },
        crfCompleted: {
          tableHeader: 'CRF completed status',
          key: 'crfCompleted',
          type: 'string',
          value: client.contractedProvider ? client.crfCompleted ? 'Yes' : 'No' : 'N/A',
          displayValue: client.contractedProvider ? client.crfCompleted ? 'Yes' : 'No' : 'N/A',
          columnWidth: '8%',
        },
      };
    });
  }, [clients, rosters]);

  return (
    <TableWrapper>
      <SortableTable
        id="incomplete-crfs-table"
        data={tableData}
        loading={loading || isLoading}
        fetching={fetching}
        loadError={loadError}
        header={tableHeaders}
        searchColumns={[]}
        searchPlaceholder=""
        excludeFilterAndSearch={true}
        sortableColumns={tableData.length > 0 ? [
          tableData[0]?.admissionDate,
          tableData[0]?.daysSinceAdmission,
          tableData[0]?.crfCompleted,
        ] : []}
        tableOverrides={{
          searchFunction,
          searchTerm,
          sortFunction,
          initialSort: {
            tableColumn: {
              tableHeader: 'Client admission date',
              key: 'clientAdmissionDate',
              type: 'date',
              value: '',
              displayValue: '',
            },
            sortOrder: SORT_ORDER.ASC,
          },
        }}
        onNextPage={onNextPage}
      />
    </TableWrapper>
  );
};

export default IncompleteCRFsTable;
